import { useEffect, useRef, useState } from 'react';
import { AppState, AppStateStatus } from 'react-native';
import remoteConfig from '@react-native-firebase/remote-config';
import DeviceInfo from 'react-native-device-info';
import { Telemetry } from 'app/services/telemetry/Telemetry';

const POLL_INTERVAL_MS = 12 * 60 * 60 * 1000;
const MIN_VERSION_KEY = 'min_version_android';
const FETCH_INTERVAL_MS = __DEV__ ? 0 : 12 * 60 * 60 * 1000;

function isOutdated(current: string, minimum: string): boolean {
  const parse = (v: string) => v.split('.').map(n => parseInt(n, 10));
  const [cMaj, cMin, cPat] = parse(current);
  const [mMaj, mMin, mPat] = parse(minimum);
  if (cMaj !== mMaj) return cMaj < mMaj;
  if (cMin !== mMin) return cMin < mMin;
  return cPat < mPat;
}

export function useVersionCheck() {
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkVersion = async () => {
    try {
      await remoteConfig().fetchAndActivate();
      const remoteValue = remoteConfig().getValue(MIN_VERSION_KEY);
      const source = remoteValue.getSource();
      if (source === 'static') return;

      const minVersion = remoteValue.asString();
      if (!minVersion || minVersion === '0.0.0') return;

      const currentVersion = DeviceInfo.getVersion();
      const outdated = isOutdated(currentVersion, minVersion);
      Telemetry.breadcrumb('version_check.result', {
        currentVersion,
        minVersion,
        outdated,
        source,
        type: 'info',
      });
      setNeedsUpdate(outdated);
    } catch (error) {
      Telemetry.error(error as Error, {
        event: 'version_check.failed',
        domain: 'core',
        context: { source: 'useVersionCheck' },
      });
    }
  };

  useEffect(() => {
    remoteConfig().setConfigSettings({ minimumFetchIntervalMillis: FETCH_INTERVAL_MS });
    remoteConfig().setDefaults({ [MIN_VERSION_KEY]: '0.0.0' });
    checkVersion();

    intervalRef.current = setInterval(checkVersion, POLL_INTERVAL_MS);

    const subscription = AppState.addEventListener('change', (state: AppStateStatus) => {
      if (state === 'active') checkVersion();
    });

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      subscription.remove();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { needsUpdate };
}
